import { useQuery } from '@tanstack/react-query';
import { EntitlementRequestErrorCode } from '@upright/common/iam/entitlement/request-api-types';
import {
  getCustomerAccessRequestsQuery,
  useCreateAccessRequestMutation,
} from '@upright/ui/api/iam/organizations/access';
import { Button } from '@upright/ui/common/generic/components/button';
import { Input } from '@upright/ui/common/generic/components/input';
import { Stack } from '@upright/ui/common/generic/components/layout';
import { Text } from '@upright/ui/common/generic/components/typography';
import { useState } from 'react';
import { findShownRequest, RequestTarget, requestStateLabels } from './requests';

interface Props {
  organizationId: number;
  target: RequestTarget;
}

/** What the paywall says once the organisation has asked, or why it cannot ask. */
function toRefusalText(code: EntitlementRequestErrorCode | null): string {
  if (code === EntitlementRequestErrorCode.ALREADY_REQUESTED) {
    return 'Someone in your organization has already requested this.';
  }
  if (code === EntitlementRequestErrorCode.ALREADY_HELD) {
    return 'Your organization already has this. Reload the page to open it.';
  }
  return 'The request did not go through. Try again.';
}

/**
 * "Request access" where the paywall offered "contact us": one click asks for the locked lens,
 * feature or company for the caller's organisation, with an optional note. While a request for it is
 * on its way, its state shows instead, so a second click cannot ask twice.
 */
export function PaywallRequestAction({ organizationId, target }: Props) {
  const requests = useQuery(getCustomerAccessRequestsQuery(organizationId));
  const create = useCreateAccessRequestMutation(organizationId);
  const [note, setNote] = useState('');
  const asked = findShownRequest(requests.data?.requests ?? [], create.data, target);

  if (asked) {
    return (
      <Text size="md" weight="semibold" color="gray-900">
        {requestStateLabels[asked.state]}
      </Text>
    );
  }
  if (create.error?.code === EntitlementRequestErrorCode.ALREADY_REQUESTED) {
    return (
      <Text size="md" color="gray-600">
        {toRefusalText(create.error.code)}
      </Text>
    );
  }
  return (
    <Stack gap="sm" align="stretch">
      <Input label="Note (optional)" value={note} onChange={setNote} maxLength={2000} />
      <Stack row gap="sm" align="center" wrap>
        <Button
          size="small"
          isPending={create.isPending}
          isDisabled={requests.isPending}
          onPress={() => {
            create.mutate({ target, note: note.trim() || undefined });
          }}
        >
          Request access
        </Button>
        {create.error && (
          <Text size="sm" color="gray-600" asChild>
            <span role="alert">{toRefusalText(create.error.code)}</span>
          </Text>
        )}
      </Stack>
    </Stack>
  );
}
